import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/lib/database.types';

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  initialized: boolean;
  loading: boolean;

  init: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    name: string,
    tel?: string,
    tipo?: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.error('[auth] fetchProfile error', error);
    return null;
  }
  return data;
}

/**
 * Llama la RPC polvillas.ensure_profile para crear el profile del usuario
 * autenticado si no existe (cubre el caso de cuentas auth.users preexistentes
 * de otras apps del mismo proyecto Supabase). Idempotente; no modifica role.
 */
async function ensureProfile(user: User): Promise<Profile | null> {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const { data, error } = await supabase.rpc('ensure_profile', {
    p_name: typeof meta.name === 'string' ? meta.name : null,
    p_tel:  typeof meta.tel  === 'string' ? meta.tel  : null,
    p_tipo: typeof meta.tipo === 'string' ? meta.tipo : null,
  });
  if (error) {
    console.error('[auth] ensure_profile error', error);
    return null;
  }
  return (data as Profile | null) ?? null;
}

async function resolveProfile(user: User | null): Promise<Profile | null> {
  if (!user) return null;
  const existing = await fetchProfile(user.id);
  if (existing) return existing;
  return await ensureProfile(user);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  profile: null,
  initialized: false,
  loading: false,

  init: async () => {
    if (get().initialized) return;
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    const profile = await resolveProfile(session?.user ?? null);
    set({ session, user: session?.user ?? null, profile, initialized: true });

    supabase.auth.onAuthStateChange(async (_event, newSession) => {
      const newProfile = await resolveProfile(newSession?.user ?? null);
      set({ session: newSession, user: newSession?.user ?? null, profile: newProfile });
    });
  },

  signIn: async (email, password) => {
    set({ loading: true });
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    set({ loading: false });
    return { error: error?.message ?? null };
  },

  signUp: async (email, password, name, tel, tipo) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, app: 'polvillas', tel, tipo } },
    });
    if (error) {
      set({ loading: false });
      return { error: error.message };
    }
    // El trigger on_auth_user_created_polvillas inserta el profile cuando
    // app='polvillas'. Llamamos ensure_profile para forzar tel/tipo y para
    // cubrir el caso de email-confirm desactivado donde ya hay sesión.
    if (data.session && data.user) {
      await supabase.rpc('ensure_profile', {
        p_name: name,
        p_tel:  tel  ?? null,
        p_tipo: tipo ?? null,
      });
    }
    set({ loading: false });
    return { error: null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null, profile: null });
  },

  refreshProfile: async () => {
    const u = get().user;
    if (!u) return;
    const p = await resolveProfile(u);
    set({ profile: p });
  },
}));
